'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Check, Crown, Zap } from 'lucide-react';
import { toast } from 'sonner';

interface RegisterFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

type Plan = 'free' | 'premium';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

const premiumFeatures = [
  'Akses i plotë në 248+ filma',
  'Cilësi 4K Ultra HD',
  'Pa reklama pre-roll',
  'Shkarkim offline në APK',
  'Histori e plote shikimi',
];

const freeFeatures = [
  '50+ filma falas',
  'Cilësi deri HD 720p',
  'Reklama para filmit',
  'Histori e kufizuar',
];

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan>('free');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const passwordVal = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setSubmitting(true);
    // backend: POST /api/auth/register + if premium → redirect to Stripe checkout
    await new Promise((r) => setTimeout(r, 1600));
    setSubmitting(false);
    if (selectedPlan === 'premium') {
      toast.success('Llogaria u krijua! Duke ju ridrejtuar te pagesa Stripe...');
    } else {
      toast.success(`Mirë se vini, ${data.fullName}! Llogaria juaj u krijua.`);
    }
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-3 py-2.5 bg-input border ${
      hasError ? 'border-red-500/50' : 'border-border'
    } rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all`;

  return (
    <div className="space-y-5 fade-in">
      <div className="text-center mb-4">
        <h1 className="text-2xl font-800 text-foreground">Krijo Llogari</h1>
        <p className="text-sm text-secondary-foreground mt-1">
          Falas — gati në 30 sekonda
        </p>
      </div>

      {/* Plan Picker */}
      <div>
        <p className="text-xs font-700 text-muted-foreground uppercase tracking-wider mb-3">
          Zgjidh Planin
        </p>
        <div className="grid grid-cols-2 gap-3">
          {/* Free Plan */}
          <button
            type="button"
            onClick={() => setSelectedPlan('free')}
            className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 ${
              selectedPlan === 'free' ?'border-foreground/40 bg-muted' :'border-border bg-muted/30 hover:border-border hover:bg-muted/50'
            }`}
          >
            {selectedPlan === 'free' && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-foreground rounded-full flex items-center justify-center">
                <Check size={11} className="text-background" />
              </div>
            )}
            <div className="flex items-center gap-1.5 mb-2">
              <Zap size={14} className="text-secondary-foreground" />
              <span className="text-sm font-700 text-foreground">Falas</span>
            </div>
            <p className="text-xl font-800 text-foreground mb-2">
              $0
              <span className="text-xs font-500 text-muted-foreground">/muaj</span>
            </p>
            <ul className="space-y-1">
              {freeFeatures.map((f) => (
                <li key={`free-feat-${f}`} className="flex items-start gap-1.5 text-xs text-secondary-foreground">
                  <span className="text-muted-foreground mt-0.5">•</span>
                  {f}
                </li>
              ))}
            </ul>
          </button>

          {/* Premium Plan */}
          <button
            type="button"
            onClick={() => setSelectedPlan('premium')}
            className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 ${
              selectedPlan === 'premium' ?'border-primary bg-primary/10' :'border-primary/30 bg-primary/5 hover:border-primary/50 hover:bg-primary/8'
            }`}
          >
            <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-primary text-primary-foreground text-xs font-700 rounded-md whitespace-nowrap">
              MË POPULLAR
            </div>
            {selectedPlan === 'premium' && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                <Check size={11} className="text-primary-foreground" />
              </div>
            )}
            <div className="flex items-center gap-1.5 mb-2">
              <Crown size={14} className="text-accent" />
              <span className="text-sm font-700 text-foreground">Premium</span>
            </div>
            <p className="text-xl font-800 text-primary mb-2">
              $4.99
              <span className="text-xs font-500 text-muted-foreground">/muaj</span>
            </p>
            <ul className="space-y-1">
              {premiumFeatures.map((f) => (
                <li key={`prem-feat-${f}`} className="flex items-start gap-1.5 text-xs text-secondary-foreground">
                  <Check size={10} className="text-green-400 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </button>
        </div>
        {selectedPlan === 'premium' && (
          <p className="mt-2 text-xs text-center text-muted-foreground">
            Pagesa e sigurt përmes{' '}
            <span className="text-accent font-600">Stripe</span> — anulo kur të dëshironi
          </p>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Full Name */}
        <div>
          <label className="block text-xs font-600 text-secondary-foreground mb-1.5">
            Emri i Plotë <span className="text-primary">*</span>
          </label>
          <input
            type="text"
            {...register('fullName', {
              required: 'Emri është i detyrueshëm',
              minLength: { value: 3, message: 'Minimumi 3 karaktere' },
            })}
            placeholder="p.sh. Ardit Kelmendi"
            className={inputClass(!!errors.fullName)}
          />
          {errors.fullName && (
            <p className="mt-1.5 text-xs text-red-400">{errors.fullName.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-xs font-600 text-secondary-foreground mb-1.5">
            Adresa Email <span className="text-primary">*</span>
          </label>
          <input
            type="email"
            {...register('email', {
              required: 'Email-i është i detyrueshëm',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Adresë email-i e pavlefshme' },
            })}
            placeholder="ju@shembull.al"
            className={inputClass(!!errors.email)}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-400">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label className="block text-xs font-600 text-secondary-foreground mb-1.5">
            Fjalëkalimi <span className="text-primary">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">
            Minimumi 8 karaktere, 1 shkronjë e madhe dhe 1 numër
          </p>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password', {
                required: 'Fjalëkalimi është i detyrueshëm',
                minLength: { value: 8, message: 'Minimumi 8 karaktere' },
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*\d).+$/,
                  message: 'Duhet të ketë 1 shkronjë të madhe dhe 1 numër',
                },
              })}
              placeholder="••••••••"
              className={`${inputClass(!!errors.password)} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Fshih' : 'Shfaq'}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-400">{errors.password.message}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-xs font-600 text-secondary-foreground mb-1.5">
            Konfirmo Fjalëkalimin <span className="text-primary">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              {...register('confirmPassword', {
                required: 'Konfirmoni fjalëkalimin',
                validate: (val) =>
                  val === passwordVal || 'Fjalëkalimet nuk përputhen',
              })}
              placeholder="••••••••"
              className={`${inputClass(!!errors.confirmPassword)} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showConfirm ? 'Fshih' : 'Shfaq'}
            >
              {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1.5 text-xs text-red-400">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Terms */}
        <div>
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="agreeTerms"
              {...register('agreeTerms', {
                required: 'Duhet të pranoni kushtet e shërbimit',
              })}
              className="rounded border-border mt-0.5"
            />
            <label htmlFor="agreeTerms" className="text-xs text-secondary-foreground leading-relaxed cursor-pointer">
              Pranoj{' '}
              <button type="button" className="text-primary hover:underline font-600">
                Kushtet e Shërbimit
              </button>{' '}
              dhe{' '}
              <button type="button" className="text-primary hover:underline font-600">
                Politikën e Privatësisë
              </button>{' '}
              të Dublime Al Shqip
            </label>
          </div>
          {errors.agreeTerms && (
            <p className="mt-1 text-xs text-red-400">{errors.agreeTerms.message}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className={`w-full flex items-center justify-center gap-2 py-3 font-700 rounded-xl active:scale-[0.98] transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed mt-1 ${
            selectedPlan === 'premium' ?'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25' :'bg-foreground text-background hover:bg-foreground/90'
          }`}
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              {selectedPlan === 'premium' ? 'Duke ridrejtuar te Stripe...' : 'Duke krijuar llogarinë...'}
            </>
          ) : selectedPlan === 'premium' ? (
            <>
              <Crown size={16} />
              Krijo Llogari Premium — $4.99/muaj
            </>
          ) : (
            <>
              <Check size={16} />
              Krijo Llogari Falas
            </>
          )}
        </button>
      </form>

      {/* Google */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">ose</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <button className="w-full flex items-center justify-center gap-2.5 py-2.5 bg-muted border border-border rounded-xl text-sm font-600 text-foreground hover:bg-border active:scale-[0.98] transition-all duration-150">
        <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        Regjistrohu me Google
      </button>

      <p className="text-center text-sm text-secondary-foreground">
        Keni llogari?{' '}
        <button
          onClick={onSwitchToLogin}
          className="text-primary font-700 hover:text-primary/80 transition-colors"
        >
          Hyr këtu
        </button>
      </p>
    </div>
  );
}